import { getDisplayEmail } from "@/lib/utils/display-email";
import { Link, useLocation } from "react-router-dom";
import { useMemo, useState } from "react";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { ApplicationBrandLogo } from "@/components/icons/ApplicationBrandLogo";
import { Avatar as AkunaAvatar } from "@akuna/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";
import { TooltipTrigger, TooltipContent, Tooltip, TooltipArrow } from "@/components/ui/tooltip";
import { useFilteredNavigation } from "@/hooks/useFilteredNavigation";
import { useFiscalEnabled } from "@/hooks/useFiscalEnabled";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { getInitials } from "@/lib/utils/string-utils";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import {
  MAIN_NAV_ITEMS,
  getMainNavTargetPath,
  isMainNavActive,
  getNavItemLabel,
} from "@/components/navigation/main-nav-items";
import { useDaycareHotelAccess } from "@/hooks/useDaycareHotelAccess";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMobileHeaderContext } from "@/contexts/MobileHeaderContext";
import { Building2 } from "lucide-react";
import { useClinicsForUser, useSwitchClinic } from "@/hooks/useClinics";
import { useCurrentClinicId } from "@/hooks/useAppointments";

function TopNavLoadingPlaceholder() {
  return (
    <div className="flex items-center gap-2 px-4 py-1.5 rounded-full">
      <Skeleton className="h-6 w-6 rounded-full" />
      <Skeleton className="h-3 w-12" />
    </div>
  );
}

interface TopNavigationProps {
  onSearchClick?: () => void;
  additionalMenuItems?: React.ReactNode;
}

export function TopNavigation({ onSearchClick, additionalMenuItems }: TopNavigationProps) {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { signOut } = useAuth();
  const { data: profile } = useProfile();
  const { t: tNav } = useTranslation("nav");
  const { t: tCommon } = useTranslation("common");
  const { config: mobileHeaderConfig } = useMobileHeaderContext();
  const { data: clinics = [] } = useClinicsForUser();
  const { data: currentClinicId } = useCurrentClinicId();
  const switchClinic = useSwitchClinic();
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);

  // Fetch filtered navigation (permissions/modules/custom logic in one place)
  const { sections, isLoading } = useFilteredNavigation();
  const { labelKey: daycareHotelLabelKey } = useDaycareHotelAccess();
  const fiscalEnabled = useFiscalEnabled();
  const { hasPermission: canViewTeam } = useHasPermissionSync("team", "view");

  // Filter navigation items based on the same rules used everywhere else
  const visibleNavItems = useMemo(() => {
    if (isLoading) return [];

    const visibleUrls = new Set(
      sections.flatMap((s) => s.items.map((i) => i.url))
    );

    return MAIN_NAV_ITEMS.filter((item) => visibleUrls.has(item.path));
  }, [sections, isLoading]);

  const canAccessGrooming = useMemo(() => {
    if (isLoading) return false;

    const visibleUrls = new Set(sections.flatMap((s) => s.items.map((i) => i.url)));
    return visibleUrls.has("/grooming");
  }, [sections, isLoading]);

  const clinicsByCustomer = useMemo(() => {
    return clinics.reduce<Record<string, typeof clinics>>((groups, clinic) => {
      groups[clinic.customer_id] = [...(groups[clinic.customer_id] ?? []), clinic];
      return groups;
    }, {});
  }, [clinics]);

  const customerIds = Object.keys(clinicsByCustomer);

  const currentClinic = clinics.find((clinic) => clinic.id === currentClinicId);

  const handleClinicChange = (clinicId: string) => {
    if (clinicId === currentClinicId || switchClinic.isPending) return;

    switchClinic.mutate(clinicId, {
      onSuccess: () => setIsAccountMenuOpen(false),
    });
  };

  if (isMobile) {
    return (
      <div className="md:hidden bg-[var(--bg-page)]">
        <header className="akuna-mobile-header">
          <div className="akuna-mobile-header__top relative">
            <div className="akuna-mobile-header__brand">
              <Link to="/calendar" className="flex items-end gap-2">
                <ApplicationBrandLogo variant="mark" className="h-[20px] w-[20px]" />
              </Link>
            </div>
            {mobileHeaderConfig.title ? (
              <div className="pointer-events-none absolute bottom-2 left-1/2 max-w-[48vw] -translate-x-1/2 truncate text-[15px] font-extrabold leading-tight text-foreground">
                {mobileHeaderConfig.title}
              </div>
            ) : null}
            <div className="akuna-mobile-header__actions !items-end">
              <div className="rounded-full border-0 bg-transparent p-0">
                <AkunaAvatar
                  className="h-9 w-9 text-sm"
                  fallback={profile?.full_name ? getInitials(profile.full_name) : "AP"}
                  name={profile?.full_name || tCommon("user")}
                  src={profile?.avatar_url || undefined}
                />
              </div>
            </div>
          </div>

          {mobileHeaderConfig.search ? (
            <div className="akuna-mobile-header__search">{mobileHeaderConfig.search}</div>
          ) : null}
        </header>
      </div>
    );
  }

  return (
    <header
      className="relative z-50 w-full shrink-0 bg-white border-border-gray border-b"
    >
      <div className="mx-auto px-5 sm:px-10 lg:px-20">
        <div className="flex h-14 items-center justify-between">
          {/* Logo */}
          <Link to="/calendar" className="flex items-center gap-2">
            <ApplicationBrandLogo variant="header" className="h-[22px] w-[82px]" />
          </Link>

          {/* Desktop Navigation - centered */}
          <nav className="hidden md:flex items-center gap-1 absolute left-1/2 transform -translate-x-1/2">
            {isLoading ? (
              <>
                {[1, 2, 3, 4].map((i) => (
                  <TopNavLoadingPlaceholder key={i} />
                ))}
              </>
            ) : (
              visibleNavItems.map((item) => {
                const active = isMainNavActive(item.path, location.pathname);
                const targetPath = getMainNavTargetPath(item.path, canAccessGrooming);

                const linkNode = (
                  <Link
                    to={targetPath}
                    className={`
                  flex items-center gap-2 px-4 py-1.5 rounded-full transition-colors
                  ${active ? 'bg-muted-foreground/10' : ''}
                      `}
                  >
                    <CustomIcons
                      name={item.icon}
                      className="h-6 w-6"
                      active={active}
                      strokeWidth={1}
                    />
                    <span
                      className={`
                    text-xs font-medium
                  ${active ? 'text-muted-foreground block' : 'hidden'}
                  `}
                    >
                      {getNavItemLabel(item, tNav, daycareHotelLabelKey)}
                    </span>
                  </Link>
                );

                // Only show tooltip for non-active items
                if (active) return <span key={item.path} className="contents">{linkNode}</span>;

                return (
                  <Tooltip key={item.path} delayDuration={100}>
                    <TooltipTrigger asChild>{linkNode}</TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      align="center"
                      className="border-transparent bg-foreground text-background"
                    >
                      {getNavItemLabel(item, tNav, daycareHotelLabelKey)}
                      <TooltipArrow className="fill-foreground" />
                    </TooltipContent>
                  </Tooltip>
                );
              })
            )}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-3">
            {/* User menu */}
            <DropdownMenu open={isAccountMenuOpen} onOpenChange={setIsAccountMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-3 rounded-full border-0 bg-transparent py-1 pl-0 pr-3 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <AkunaAvatar
                    className="h-9 w-9 text-sm"
                    fallback={profile?.full_name ? getInitials(profile.full_name) : "AP"}
                    name={profile?.full_name || tCommon("user")}
                    src={profile?.avatar_url || undefined}
                  />
                  {profile ? (
                    <div className="hidden lg:flex max-w-[200px] flex-col justify-center leading-tight">
                      <span className="w-full truncate text-left text-sm font-medium text-foreground">
                        {profile.full_name || tCommon("user")}
                      </span>
                      {clinics.length > 1 && currentClinic ? (
                        <span className="w-full truncate text-left text-xs text-muted-foreground">
                          {currentClinic.name}
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <div className="hidden lg:flex flex-col gap-1">
                      <Skeleton className="h-3.5 w-24" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{profile?.full_name || tCommon("user")}</span>
                    <span className="text-xs text-muted-foreground">{getDisplayEmail(profile?.email)}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className="hover:bg-secondary focus:bg-secondary text-color-dark hover:text-color-dark focus:text-color-dark">
                  <Link to="/profile">{tNav("profile")}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="hover:bg-secondary focus:bg-secondary text-color-dark hover:text-color-dark focus:text-color-dark">
                  <Link to="/settings">{tNav("settings")}</Link>
                </DropdownMenuItem>
                {canViewTeam && (
                  <DropdownMenuItem asChild className="hover:bg-secondary focus:bg-secondary text-color-dark hover:text-color-dark focus:text-color-dark">
                    <Link to="/team">{tNav("team")}</Link>
                  </DropdownMenuItem>
                )}
                {fiscalEnabled && (
                  <DropdownMenuItem asChild className="hover:bg-secondary focus:bg-secondary text-color-dark hover:text-color-dark focus:text-color-dark">
                    <Link to="/taxes/settings">{tNav("taxes")}</Link>
                  </DropdownMenuItem>
                )}
                {clinics.length > 1 ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>{tNav("accountMenu.selectClinic")}</DropdownMenuLabel>
                    {customerIds.map((customerId) => {
                      const customerClinics = clinicsByCustomer[customerId];
                      const showCustomerName = customerIds.length > 1;

                      return (
                        <div key={customerId}>
                          {showCustomerName ? (
                            <div className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              {customerClinics[0]?.customer_name}
                            </div>
                          ) : null}
                          {customerClinics.map((clinic) => {
                            const isActiveClinic = clinic.id === currentClinicId;

                            return (
                              <DropdownMenuItem
                                key={clinic.id}
                                disabled={isActiveClinic || switchClinic.isPending}
                                aria-current={isActiveClinic ? "true" : undefined}
                                className="gap-2"
                                onClick={() => handleClinicChange(clinic.id)}
                              >
                                <Building2 className="h-4 w-4" />
                                <span className="min-w-0 flex-1 truncate">{clinic.name}</span>
                                {isActiveClinic ? <span className="sr-only">{tNav("accountMenu.activeClinic")}</span> : null}
                                {switchClinic.isPending && !isActiveClinic ? (
                                  <span className="text-xs text-muted-foreground">{tNav("accountMenu.switchingClinic")}</span>
                                ) : null}
                              </DropdownMenuItem>
                            );
                          })}
                        </div>
                      );
                    })}
                  </>
                ) : null}
                <DropdownMenuSeparator />
                {additionalMenuItems}
                <DropdownMenuItem onClick={() => signOut()} className="text-destructive">
                  {tNav("signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
